import { useCallback, useRef } from 'react';

/**
 * Shell-style up/down recall for a text input.
 *
 * Up steps to older entries and Down to newer ones. The text in the field when Up is first
 * pressed is kept as a draft, and Down past the newest entry restores it. Navigation runs over a
 * snapshot of the history taken at that first Up, so the list can't shift mid-navigation.
 *
 * Editing a recalled entry leaves history: Up/Down go back to the browser and never replace the
 * edited text, so an edit can't be lost. Emptying the field, or remounting the input, starts
 * history afresh from the newest entry.
 *
 * Only plain Up/Down are handled. Modified arrows and IME composition are left to the browser, and
 * so are the ends of the list. The event is never stopped from propagating: canvas shortcuts and
 * the Tour stay out of the way through their own keyboard-focus gates.
 *
 * In a multi-line field Up/Down also move the caret between lines, so with `multiline` they recall
 * only from the first line (Up) or the last line (Down), and a selection is left to the browser.
 * Lines are split at newlines: a long line wrapping on screen counts as one, so Up anywhere in a
 * wrapped first line recalls (and Down brings the draft back).
 *
 * Position is kept in refs and resets when the input remounts. Call reset() when the history or the
 * field changes some other way (an entry removed, or one put in the field from a menu), so the next
 * Up starts from the current history.
 *
 * @param {object} args
 * @param {() => string[]} args.getEntries most recent first, e.g. from createInputHistory
 * @param {string} args.value the field's current text
 * @param {(text: string) => void} args.setValue
 * @param {boolean} [args.multiline] the field takes several lines (a textarea)
 * @returns {{ onKeyDown: (evt: KeyboardEvent) => void, reset: () => void }}
 */
export default function useInputHistory({
    getEntries, value, setValue, multiline=false,
}) {
    const snapshot = useRef(null); // null while at the draft
    const index = useRef(-1); // -1 is the draft
    const draft = useRef('');
    const recalled = useRef(''); // the entry this hook last put in the field

    const onKeyDown = useCallback((evt) => {
        if (evt.key !== 'ArrowUp' && evt.key !== 'ArrowDown') return;
        if (evt.ctrlKey || evt.altKey || evt.metaKey || evt.shiftKey) return;
        if (evt.nativeEvent?.isComposing || evt.isComposing) return;
        if (multiline && caretMovesBetweenLines(evt)) return;

        if (index.current !== -1 && value !== recalled.current) {
            if (value) return; // a recalled entry was edited: out of history
            index.current = -1; // emptied: start afresh
            snapshot.current = null;
        }

        const entries = snapshot.current || getEntries();
        const next = index.current + (evt.key === 'ArrowUp' ? 1 : -1);
        if (next < -1 || next >= entries.length) return;

        if (index.current === -1) {
            draft.current = value;
            snapshot.current = entries;
        }
        index.current = next;
        if (next === -1) snapshot.current = null;

        evt.preventDefault(); // otherwise the caret also jumps to the start of the field
        if (next === -1) {
            setValue(draft.current);
        } else {
            recalled.current = entries[next];
            setValue(entries[next]);
        }
    }, [getEntries, value, setValue, multiline]);

    const reset = useCallback(() => {
        index.current = -1;
        snapshot.current = null;
    }, []);

    return { onKeyDown, reset };
}

// Should this Up/Down move the caret within a multi-line field, rather than recall? True when text
// is selected, or when there is a line above the caret (Up) or below it (Down).
function caretMovesBetweenLines(evt) {
    const { selectionStart: start, selectionEnd: end, value: text } = evt.target;
    if (typeof start !== 'number' || typeof end !== 'number' || typeof text !== 'string') {
        return false;
    }
    if (start !== end) return true;
    return evt.key === 'ArrowUp'
        ? text.slice(0, start).includes('\n')
        : text.slice(end).includes('\n');
}
